import { useCallback } from "react";
import { useMessages } from "../providers/MessagesContext";
import { useAuth } from "../providers/AuthContext";
import { wrapTyping, shouldBlockActions } from "../utils/typingGate";
import {
  makeResolveVirtualRequest,
  makeFlagMessageRequest,
  preFetchMediaAndAudio,
} from "../services/ChatService";
import { CHAT_CONSTANTS } from "../utils/constants";
import { ChatMessage } from "../types/chat";

export const useMessageActions = () => {
  const {
    messages,
    addMessage,
    addMessageObject,
    updateMessageFlag,
    setNewDecisionMessage,
    addToStack,
    openFeedbackModal,
    sessionCreateTime,
  } = useMessages();
  const { getUserEmail } = useAuth();

  const onOptionClick = useCallback(
    async (
      nodeUuid: string,
      textClicked: string,
      parentNodeUuid: string,
      elementId: string,
      isDefinitionNode: boolean
    ): Promise<void> => {
      if (shouldBlockActions()) {
        return;
      }

      const userMessageId = crypto.randomUUID();
      if (!isDefinitionNode) {
        addMessage(textClicked, "user", userMessageId);
      }
      // If the user clicked a suggested next step, don't set that as the decision message
      if (!textClicked.includes("[Next]") && !isDefinitionNode) {
        setNewDecisionMessage(parentNodeUuid, nodeUuid);
      }

      let responseWithPreFetched: ChatMessage;
      try {
        const isFirstMessage = messages.length === 1;
        responseWithPreFetched = await wrapTyping(async () => {
          const startTime = Date.now();
          const rawResponse = await makeResolveVirtualRequest({
            nodeUUID: nodeUuid,
            textClicked: textClicked,
            currentNodeId: parentNodeUuid,
            queryId: userMessageId,
            isOptionClick: true,
            userEmail: getUserEmail(),
            logRequest: true,
            id: elementId,
            prefix: "benchmark",
            endpointResult: isDefinitionNode
              ? "definition"
              : "user clicked on option",
            isFirstMessage: isFirstMessage,
          });
          const preFetched = await preFetchMediaAndAudio(rawResponse);

          // Ensure at least 0.5s total so the typing animation doesn't look choppy
          const elapsed = Date.now() - startTime;
          if (elapsed < 500) {
            await new Promise((resolve) => setTimeout(resolve, 500 - elapsed));
          }
          return preFetched;
        });
      } catch (error) {
        console.error("Resolve virtual request error:", error);
        const errorMessage = {
          id: crypto.randomUUID(),
          messageId: userMessageId,
          text: CHAT_CONSTANTS.ERROR_MESSAGE,
          messageType: "bot" as const,
          dateTime: new Date(),
          nodeUuid: "00000000-0000-0000-0000-000000000000",
          isFlagged: false,
        };
        addMessageObject(errorMessage);
        return;
      }
      addToStack(nodeUuid, userMessageId);
      addMessageObject(responseWithPreFetched);
    },
    [setNewDecisionMessage, getUserEmail, addToStack, addMessageObject]
  );

  const onSpanClick = useCallback(
    async (
      dataId: string,
      spanText: string,
      elementId: string,
      dataInfo: string
    ): Promise<void> => {
      if (shouldBlockActions()) {
        return;
      }
      const userMessageId = crypto.randomUUID();
      const isFirstMessage = messages.length === 1;
      if (dataInfo != CHAT_CONSTANTS.DEFINITION_ROOT) {
        // Don't add a user message if a user clicked a definition which is treated as a pop out rather than new message
        addMessage(spanText, "user", userMessageId);
      }

      const lastBotMessage = messages
        .filter((msg) => msg.messageType === "bot")
        .pop();
      const currentNodeId = lastBotMessage?.nodeUuid || "";

      let responseWithPreFetched: ChatMessage;
      try {
        responseWithPreFetched = await wrapTyping(async () => {
          const startTime = Date.now();
          const rawResponse = await makeResolveVirtualRequest({
            nodeUUID: dataId,
            textClicked: spanText,
            currentNodeId: currentNodeId,
            queryId: userMessageId,
            isOptionClick: false,
            userEmail: getUserEmail(),
            logRequest: true,
            id: elementId,
            prefix: "benchmark",
            endpointResult:
              dataInfo == CHAT_CONSTANTS.DEFINITION_ROOT && dataId != dataInfo
                ? "definition"
                : "",
            isFirstMessage: isFirstMessage,
          });
          const preFetched = await preFetchMediaAndAudio(rawResponse);

          // Ensure at least 0.5s total so the typing animation doesn't look choppy
          const elapsed = Date.now() - startTime;
          if (elapsed < 500) {
            await new Promise((resolve) => setTimeout(resolve, 500 - elapsed));
          }
          return preFetched;
        });
      } catch (error) {
        console.error("Resolve virtual request error:", error);
        addMessage(CHAT_CONSTANTS.ERROR_MESSAGE, "bot", userMessageId);
        return;
      }
      // if the endpoint result is definition, then just pop up a modal rather than adding it to the messages
      if (!responseWithPreFetched?.endpoint_result?.includes("definition")) {
        addToStack(dataId, userMessageId);
      }
      addMessageObject(responseWithPreFetched);
    },
    [messages, addMessage, getUserEmail, addToStack, addMessageObject]
  );

  const onFlagClick = useCallback(
    (messageId: string) => {
      openFeedbackModal(messageId);
    },
    [openFeedbackModal]
  );

  const onFeedbackSubmit = useCallback(
    async (messageId: string, feedback: string) => {
      await wrapTyping(async () => {
        try {
          const response = await makeFlagMessageRequest({
            query_id: messageId,
            user_feedback: feedback,
            session_create_time: sessionCreateTime,
          });

          updateMessageFlag(messageId, true);

          addMessage(
            response.text,
            "bot",
            response.query_id,
            "",
            [],
            "00000000-0000-0000-0000-000000000000",
            [],
            "Message Feedback"
          );
        } catch (error) {
          console.error("Flag request error:", error);
          addMessage(
            CHAT_CONSTANTS.MESSAGES.FEEDBACK_ERROR,
            "bot",
            messageId,
            "",
            [],
            "00000000-0000-0000-0000-000000000000",
            [],
            "Message Feedback Error"
          );
        }
      });
    },
    [sessionCreateTime, updateMessageFlag, addMessage]
  );

  return {
    onOptionClick,
    onSpanClick,
    onFlagClick,
    onFeedbackSubmit,
  };
};
